import { NzTreeNodeOptions } from "ng-zorro-antd/tree";
import { IInsightSource, IInsightState, IInsightboardItemSetting } from "@gtmhub/insightboards/models/models";
import {
  IConnectionData,
  IInsightFilters,
  IInsightFormatting,
  IInsightQueryLayout,
  IInsightSelectorData,
  ISuggestedInsight,
  InsightFeedbackType,
} from "@gtmhub/insights";
import { ISubmitInsightEmailContent, ISuggestedMarketplaceInsightTemplate, SupportedOperators } from "@gtmhub/insights/models/models";
import { IInsightCalculationData } from "@gtmhub/sockets";
import { UserAssignee } from "@webapp/assignees/models/assignee.models";
import { Entity } from "@webapp/data-story/services/entities-facade/entities-facade.models";
import { IDaxQueryResult } from "@webapp/integrations/models/powerbi-models";
import { IAccess } from "@webapp/sessions/models/sessions.model";

export enum InsightSelectorActionType {
  BROWSE_CONNECTIONS = "browseConnections",
  BROWSE_INSIGHTS = "browseInsights",
  CONFIGURE_POWER_BI = "configurePowerBI",
  CHANGE_STATE = "changeState",
  BUILD_INSIGHT = "buildInsight",
  EXECUTE_INSIGHT = "executeInsight",
  UPDATE_INSIGHT_SELECTOR_DATA = "updateInsightSelectorData",
  OPEN_POWERBI_INSIGHT = "openPowerBIInsight",
}

export interface BrowseInsightsContext {
  connectionData: IConnectionData;
}

export interface ExecuteInsightContext {
  insight: Insight;
  data?: IInsightSelectorData;
}

export interface BuildInsightContext {
  insight?: Insight;
  connectionData: IConnectionData;
  canGoBack: boolean;
  daxQueryResult?: IDaxQueryResult;
}

export interface BrowseConnectionsActionsParams {
  action: InsightSelectorActionType.BROWSE_CONNECTIONS;
}

export interface BrowseInsightsActionParams {
  action: InsightSelectorActionType.BROWSE_INSIGHTS;
  connectionData: IConnectionData;
}

export interface BuildInsightActionParams {
  action: InsightSelectorActionType.BUILD_INSIGHT;
  connectionData: IConnectionData;
  insight?: Insight;
  daxQueryResult?: IDaxQueryResult;
  canGoBack?: boolean;
}

export interface ExecuteInsightActionParams {
  action: InsightSelectorActionType.EXECUTE_INSIGHT;
  insight: Insight;
  data?: IInsightSelectorData;
}

export interface UpdateInsightSelectorDataActionParams {
  action: InsightSelectorActionType.UPDATE_INSIGHT_SELECTOR_DATA;
  settings: DynamicItemSettings[];
  insight: Insight;
  calculatedValue: number;
  fieldName: string;
}

export interface ConfigurePowerBIActionParams {
  action: InsightSelectorActionType.CONFIGURE_POWER_BI;
  connectionData: IConnectionData;
}

export interface OpenPowerBIInsightActionParams {
  action: InsightSelectorActionType.OPEN_POWERBI_INSIGHT;
  connectionData: IConnectionData;
  daxQueryResult: IDaxQueryResult;
}

export type InsightsBrowseConnectionsOutputActionsParams = BrowseInsightsActionParams | ConfigurePowerBIActionParams;

export type BrowseInsightsListOutputActionsParams = BrowseConnectionsActionsParams | BuildInsightActionParams | ExecuteInsightActionParams;

export type ExecuteInsightsListOutputActionsParams = UpdateInsightSelectorDataActionParams | BrowseInsightsActionParams | BrowseConnectionsActionsParams;

export type BuildInsightsListOutputActionsParams = BrowseInsightsActionParams | BrowseConnectionsActionsParams | UpdateInsightSelectorDataActionParams;

export type InsightSelectorOutputActionsParams = UpdateInsightSelectorDataActionParams | ConfigurePowerBIActionParams | OpenPowerBIInsightActionParams;

export type AllInsightsListOutputActionsParams =
  | BrowseConnectionsActionsParams
  | BrowseInsightsActionParams
  | BuildInsightActionParams
  | ExecuteInsightActionParams
  | UpdateInsightSelectorDataActionParams
  | ConfigurePowerBIActionParams
  | OpenPowerBIInsightActionParams;

export interface InsightsQueryParams {
  categoryId?: string;
  insightIds?: string;
  fields?: string;
  skip?: number;
  take?: number;
  stringSearch?: string;
  sort?: string;
  isScalar?: boolean;
  usedConnectors?: string[];
}

export interface InsightDTO {
  id: string;
  // shows if currently there is a process going on with the given insight
  processing?: boolean | "deleted";
  accountId: string;
  name: string;
  title: string;
  description: string;
  categoryId: string;
  requiredEntities: Entity[];
  status: string;

  createdById?: string;
  dateCreated?: string;
  modifiedById?: string;
  dateUpdated?: string;
  totalCount?: number;
  source?: IInsightSource;

  view: string;

  formatting?: IInsightFormatting;

  queryLayout?: IInsightQueryLayout;

  language: string;
  algorithm: string;
  filters?: InsightFilters | DynamicItemSettings[];
  size?: {
    width: number;
    height: number;
  };
  direct?: boolean;
  isScalar?: boolean;
  usedFilters?: string[];
  usedEntities?: InsightUsedEntity[];
  aggregateAccess?: InsightEntityAccess[];
}

export interface Insight extends InsightDTO {
  createdBy?: UserAssignee;
  modifiedBy?: UserAssignee;
  state?: IInsightState;
  submitEmailContent?: ISubmitInsightEmailContent;
  connection?: IConnectionData;
  isShadow?: boolean;
}

export interface InsightCalculationTransaction {
  transactionId: string;
}

export interface InsightFilters {
  [filterKey: string]: string;
}

export interface DynamicItemSettings {
  name: string;
  value: string;
  title?: string;
  valueDisplayName?: string;
  type?: string;
}

export interface InsightSelectorData {
  insightName: string;
  fieldName: string;
  settings: DynamicItemSettings[];
  initialValue?: number;
  datasourceBlueprintName?: string;
  datasourceBlueprintLogoUrl?: string;
  formatting?: IInsightFormatting;
  marketplacePredictionId?: string;
  accountPredictionId?: string;
  insightTemplate?: ISuggestedMarketplaceInsightTemplate | ISuggestedInsight;
  suggestedInsights?: Array<ISuggestedInsight | ISuggestedMarketplaceInsightTemplate>;
  type?: InsightFeedbackType;
  isInsightFromAccount?: boolean;
  suggestedAccountCount?: number;
  suggestedMarketplaceCount?: number;
}

export interface InsightListFilterOption {
  value: string;
  title?: string;
}

export enum ConditionalOperatorEnum {
  AND = "AND",
  OR = "OR",
}

export type ConditionalOperator = keyof typeof ConditionalOperatorEnum;

export interface InsightCalculationPayload {
  language: string;
  script: string;
  filters: IInsightFilters;
  settings: IInsightboardItemSetting[];
  id?: string;
}

export interface DataSourceNode extends NzTreeNodeOptions {
  dataSource: string;
  logoUrl?: string;
  isLookerPlaceholder?: boolean;
  children?: DataSourceNode[];
}

export interface BuildInsightSqlOperatorWithLocalisation {
  value: SupportedOperators;
  label: string;
}

export enum LookerDataSourceType {
  DASHBOARD = "dashboard",
  LOOK = "look",
}

export interface BuildInsightSetDataSourceParams {
  getEntityFields?: boolean;
}

export interface InsightUsedEntity {
  name: string;
  dataSourceBlueprintName: string;
  dataSourceBlueprintLogoUrl?: string;
  remoteEntityName: string;
  requiredFields: InsightEntityRequiredField[];
}

interface InsightEntityRequiredField {
  name: string;
}

export interface InsightResponseMap {
  [transactionId: string]: IInsightCalculationData;
}

export interface StringMap {
  [id: string]: string;
}

export type KpiInsightUsage = { id: string; name: string };
export type InsightboardInsightUsage = { id: string; name: string };
export type MetricInsightUsage = { id: string; goalId: string; sessionId: string; name: string };
export type InsightUsage = KpiInsightUsage | InsightboardInsightUsage | MetricInsightUsage;

export type InsightEntityAccess = {
  access: IAccess;
};

export type RestrictedInsightsStatus = {
  restrictedInsightsExist: boolean;
};
